/// <amd-module name="Wit/UI/ComponentProviders/WorkItemStateProvider" />
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { isWorkItemIdType, WorkItemId } from "../Client";
import { BaseProviderComponent } from "./BaseProvider/BaseProviderComponent";
import { IWorkItemStateProviderProps, WorkItemStateInfo } from "./WorkItemStateProvider.Props";

export interface IWorkItemStateProviderState {
    stateInfo: WorkItemStateInfo | null;
    stateAndColor: WorkItemStateAndColor | null;
}

/**
 * Component to display work item state value with the state color.
 */
export class WorkItemStateProvider extends BaseProviderComponent<IWorkItemStateProviderProps, IWorkItemStateProviderState> {
    constructor(props: IWorkItemStateProviderProps) {
        super(props);

        this._setStateFromProps();
    }

    public render(): JSX.Element | null {
        const { stateAndColor } = this.state;
        if (!stateAndColor) {
            // Render nothing if state and icon data is not yet ready.
            return null;
        }

        const { children } = this.props;
        return children ? children(stateAndColor) : null;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (!this.state.stateAndColor?.fill) {
            this._initializeStateColor();
        }
    }

    public componentDidUpdate(prevProps: IWorkItemStateProviderProps): void {
        if (this._didPropsChange(prevProps)) {
            this._setStateFromProps();
        }
    }

    public shouldComponentUpdate(prevProps: IWorkItemStateProviderProps, prevState: IWorkItemStateProviderState): boolean {
        return (
            this._didPropsChange(prevProps) || prevState.stateInfo !== this.state.stateInfo || prevState.stateAndColor !== this.state.stateAndColor
        );
    }

    private _didPropsChange(prevProps: IWorkItemStateProviderProps): boolean {
        return prevProps.dataSource !== this.props.dataSource || prevProps.dataProvider !== this.props.dataProvider;
    }

    /** Gets information on the State field from the data source item, and sets/updates react state */
    private _setStateFromProps() {
        const stateInfo = this._getStateInfo(this.props.dataSource);

        if (stateInfo instanceof Promise) {
            stateInfo.then(
                info => {
                    if (this.componentMounted()) {
                        this.setState({ stateInfo: info });
                        this._initializeStateColor();
                    }
                },
                () => {
                    /* No op if rejected */
                }
            );

            if (!this.componentMounted()) {
                this.state = {
                    stateInfo: null,
                    stateAndColor: null
                };
            }
        } else {
            if (this.componentMounted()) {
                this._initializeStateColor(stateInfo!);
            } else {
                // optimize the case where the work item type data has already been loaded
                // so provider doesn't need to go through render -> mount -> render
                let stateAndColor: WorkItemStateAndColor | null = null;
                if (stateInfo) {
                    const stateAndIconData = this.props.dataProvider.getWorkItemStateAndColor(
                        stateInfo.projectIdOrName,
                        stateInfo.workItemTypeName,
                        stateInfo.stateName
                    );
                    if (!(stateAndIconData instanceof Promise)) {
                        stateAndColor = stateAndIconData;
                    }
                }

                this.state = {
                    stateInfo,
                    stateAndColor: stateAndColor ?? (stateInfo ? { name: stateInfo.stateName } : null)
                };
            }
        }
    }

    private _getStateInfo(dataSource: WorkItemId | WorkItem | WorkItemStateInfo): Promise<WorkItemStateInfo | null> | (WorkItemStateInfo | null) {
        if (!dataSource) {
            return null;
        }

        if (isWorkItemIdType(dataSource)) {
            return this.props.dataProvider.getWorkItem(dataSource as WorkItemId).then((workItem: Readonly<WorkItem> | null) => {
                return workItem ? this._createStateInfoFromWorkItem(workItem) : null;
            });
        } else if ((dataSource as WorkItemStateInfo).stateName !== undefined
            // For CSV import queries "fields" don't exist, so we can't create state info
            || !(dataSource as WorkItem).fields) {
            return dataSource as WorkItemStateInfo;
        } else {
            return this._createStateInfoFromWorkItem(dataSource as WorkItem);
        }
    }

    private _createStateInfoFromWorkItem(workItem: WorkItem): WorkItemStateInfo {
        return {
            stateName: workItem.fields[CoreFieldRefNames.State],
            projectIdOrName: workItem.fields[CoreFieldRefNames.TeamProject],
            workItemTypeName: workItem.fields[CoreFieldRefNames.WorkItemType]
        };
    }

    private async _initializeStateColor(newStateInfo?: WorkItemStateInfo): Promise<void> {
        const stateInfo = newStateInfo ? newStateInfo : this.state.stateInfo;
        if (!stateInfo) {
            // No op if state info is not ready
            return;
        }

        try {
            const { projectIdOrName, workItemTypeName, stateName } = stateInfo;
            const { dataProvider } = this.props;
            const stateAndColor: WorkItemStateAndColor = await dataProvider.getWorkItemStateAndColor(projectIdOrName, workItemTypeName, stateName);
            if (this.componentMounted()) {
                this.setState({ stateAndColor: stateAndColor });
            }
        } catch {
            if (this.componentMounted()) {
                this.setState({ stateAndColor: { name: stateInfo.stateName } });
            }
        }
    }
}
